import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Compass } from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { SEO as META, PRIMARY_AREAS, EXTENDED_AREAS } from "../content";

export default function ServiceAreas() {
  return (
    <>
      <SEO {...META.serviceAreas} />

      {/* HERO */}
      <section
        className="relative isolate flex min-h-[55vh] items-center overflow-hidden md:min-h-[60vh]"
        data-testid="areas-hero"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/hero-service-areas.jpg"
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-scrim-light" />
        </div>
        <div className="container-pad py-20 md:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/90 backdrop-blur-sm">
                Service Areas
              </span>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Where We Deliver
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                Headquartered in Milton. Serving the Greater Toronto Area and beyond.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* COVERAGE */}
      <section
        className="section-pad bg-white"
        data-testid="areas-coverage-section"
      >
        <div className="container-pad">
          <SectionReveal>
            <div className="mx-auto max-w-3xl text-center">
              <span className="overline">Coverage</span>
              <h2 className="h2 mt-3">
                Local roots. Regional reach.
              </h2>
              <p className="body-base mt-6 text-ridge-text">
                Ridgehawk Logistics is based in Milton, Ontario — strategically
                positioned at the crossroads of Highway 401 and the Niagara
                Escarpment, with fast access to the entire Greater Toronto Area.
              </p>
            </div>
          </SectionReveal>

          {/* Two column area lists */}
          <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
            <SectionReveal>
              <div
                className="h-full rounded-md border border-ridge-border bg-ridge-stone p-8 md:p-10"
                data-testid="primary-areas-card"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                    <MapPin className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ridge-amber">
                    Daily Coverage
                  </span>
                </div>
                <h3 className="font-display mt-6 text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
                  Primary Service Area
                </h3>
                <span className="accent-rule mt-5 block" />
                <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {PRIMARY_AREAS.map((area) => (
                    <li
                      key={area}
                      className="flex items-center gap-3 text-sm font-medium text-ridge-text md:text-base"
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-ridge-amber"
                        aria-hidden="true"
                      />
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.08}>
              <div
                className="h-full rounded-md border border-ridge-border bg-white p-8 md:p-10"
                data-testid="extended-areas-card"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md border border-ridge-charcoal/15 bg-white text-ridge-charcoal">
                    <Compass className="h-5 w-5 text-ridge-amber" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ridge-amber">
                    By Arrangement
                  </span>
                </div>
                <h3 className="font-display mt-6 text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
                  Extended Service Area
                </h3>
                <span className="accent-rule mt-5 block" />
                <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {EXTENDED_AREAS.map((area) => (
                    <li
                      key={area}
                      className="flex items-center gap-3 text-sm font-medium text-ridge-text md:text-base"
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-ridge-charcoal"
                        aria-hidden="true"
                      />
                      {area}
                    </li>
                  ))}
                </ul>
                <p className="mt-10 border-t border-ridge-border pt-6 text-sm leading-relaxed text-ridge-muted">
                  For dedicated route contracts and recurring medical transport, we
                  serve custom coverage areas tailored to your needs.
                </p>
              </div>
            </SectionReveal>
          </div>

          {/* Custom note + CTA */}
          <SectionReveal delay={0.1}>
            <div
              className="mt-14 flex flex-col items-start justify-between gap-6 rounded-md border border-ridge-charcoal bg-ridge-charcoal p-8 text-white md:flex-row md:items-center md:p-10"
              data-testid="areas-cta-block"
            >
              <div className="max-w-2xl">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ridge-amber">
                  Custom coverage
                </span>
                <h3 className="font-display mt-3 text-2xl font-bold leading-tight tracking-tight text-white md:text-3xl">
                  Don't see your city? Let's talk routes.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                  Recurring medical, B2B, or dedicated runs can extend beyond our
                  standard map. Tell us what you need.
                </p>
              </div>
              <Link
                to="/quote"
                className="btn-accent"
                data-testid="areas-cta-quote"
              >
                Check Coverage for Your Route
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
